import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import api from "../api/api"; // Axios instance
import logger from "../logger/logger";
import { ButtonStyled, PrimaryButtonStyled, SecondaryButtonStyled, SimpleButtonStyled } from "../styles/Button.js";
import { ErrorMessageStyled, FormNavContainerStyled, InputStyled } from "../styles/Form.js";
import { TableStyled, TableHeaderStyled, TableDataStyled, TableRowStyled } from '../styles/Table';
import { PageHeader2Styled, PageHeaderSubTextMobileStyled, PageHeaderSubTextStyled } from "../styles/Main.js";
import { StatusMessageAreaStyled } from "../styles/Zip.js";


const ZipDataTableMobile = ({ mode, zipCodes, handleIncrementFormStep, handleDecrementFormStep, formData, setFormData, errors }) => {
  const [showAllZips, setShowAllZips] = useState(false);
  const [liveStatus, setLiveStatus] = useState("");

  const liveStatusRef = useRef(null);
  useEffect(() => {
    if (liveStatusRef.current) {
      liveStatusRef.current.scrollTop = liveStatusRef.current.scrollHeight;
    }
  }, [liveStatus]);

  // --- Force Refresh Handlers ---
  const forceRefresh = async (field) => {
    try {
      const response = await api.post(`/force-refresh/${field}`, { zip_codes: zipCodes });
      logger.info(response.data.message);
      setLiveStatus((prev) => prev + response.data.message + "\n");
    } catch (error) {
      logger.error("Force refresh failed:", error);
      setLiveStatus((prev) => prev + "Force refresh failed for " + field + ".\n");
    }
  };

  const handleZipDataChange = (zip, field, value) => {
    let newFormData = { ...formData }
    newFormData[zip] = { ...formData[zip] }
    newFormData[zip][field].value = value;
    setFormData(newFormData);
    logger.debug(`Form field updated: ${field} with value ${value}`);
  };

  return (
    <div>

      {mode === "zip" && (
        <>
          <PageHeader2Styled>ZIP Code Specific Data</PageHeader2Styled>
          <>  
              <PageHeaderSubTextStyled>Confirm the data gathered for below inputs</PageHeaderSubTextStyled>
              <PageHeaderSubTextMobileStyled>Confirm the data gathered for below inputs</PageHeaderSubTextMobileStyled>
            </>
          {Object.keys(formData).length > 4 && (
            <SimpleButtonStyled style={{marginLeft: '0px', marginTop: '0px'}} onClick={() => setShowAllZips(!showAllZips)}>
              {showAllZips ? "Hide Extra ZIP Codes" : "Show All ZIP Codes"}
            </SimpleButtonStyled>
          )} 
          <TableStyled>
            <thead>
              <TableRowStyled>
                <TableHeaderStyled>ZIP Code</TableHeaderStyled>
                <TableHeaderStyled>Rent per Sqft</TableHeaderStyled>
                <TableHeaderStyled>Property Tax Rate (%)</TableHeaderStyled>
                <TableHeaderStyled>Homeowners Insurance Cost ($)</TableHeaderStyled>
              </TableRowStyled>
            </thead>
            <tbody>
              {Object.keys(formData).length > 0 ? (
                (showAllZips ? Object.keys(formData) : Object.keys(formData).slice(0, 4)).map((zip) => (
                  <React.Fragment key={zip}>
                    <TableRowStyled>
                      <TableDataStyled>{zip}</TableDataStyled>
                      <TableDataStyled>
                        <InputStyled
                          type="number"
                          value={formData[zip]?.rent_per_sqft.value || ""}
                          onChange={(e) => handleZipDataChange(zip, "rent_per_sqft", e.target.value)}
                        />
                      </TableDataStyled>
                      <TableDataStyled>
                        <InputStyled
                          type="number"
                          value={formData[zip]?.property_tax_rate_pct.value || ""}
                          onChange={(e) => handleZipDataChange(zip, "property_tax_rate_pct", e.target.value)}
                        />
                      </TableDataStyled>
                      <TableDataStyled>
                        <InputStyled
                          type="number"
                          value={formData[zip]?.homeowners_insurance_cost.value || ""}
                          onChange={(e) => handleZipDataChange(zip, "homeowners_insurance_cost", e.target.value)}
                        />
                      </TableDataStyled>
                    </TableRowStyled>
                  </React.Fragment>
                ))
              ) : (
                <TableRowStyled>
                  <TableDataStyled>No ZIP data available.</TableDataStyled>
                </TableRowStyled>
              )}
            </tbody>
          </TableStyled>
          <TableStyled>
            <thead>
                <TableRowStyled>
                    <TableHeaderStyled>Zip Code</TableHeaderStyled>
                    <TableHeaderStyled>Monthly Avg Utility Cost ($)</TableHeaderStyled>
                    <TableHeaderStyled>Annual Property Tax Increase (%)</TableHeaderStyled>
                    <TableHeaderStyled>Average Cap Rate (%)</TableHeaderStyled>
                </TableRowStyled>
            </thead>
            <tbody>
              {Object.keys(formData).length > 0 ? (
                (showAllZips ? Object.keys(formData) : Object.keys(formData).slice(0, 4)).map((zip) => (
                  <React.Fragment key={zip}>
                    <TableRowStyled>
                      <TableDataStyled>{zip}</TableDataStyled>
                      <TableDataStyled>
                        <InputStyled
                          type="number"
                          value={formData[zip]?.monthly_utilities_cost.value || ""}
                          onChange={(e) => handleZipDataChange(zip, "monthly_utilities_cost", e.target.value)}
                        />
                      </TableDataStyled>
                      <TableDataStyled>
                        <InputStyled
                          type="number"
                          value={formData[zip]?.annual_property_tax_increase_pct.value || ""}
                          onChange={(e) => handleZipDataChange(zip, "annual_property_tax_increase_pct", e.target.value)}
                        />
                      </TableDataStyled>
                      <TableDataStyled>
                        <InputStyled
                          type="number"
                          value={formData[zip]?.cap_rate.value || ""}
                          onChange={(e) => handleZipDataChange(zip, "cap_rate", e.target.value)}
                        />
                      </TableDataStyled>
                    </TableRowStyled>
                  </React.Fragment>
                ))
              ) : (
                <TableRowStyled>
                  <TableDataStyled>No ZIP data available.</TableDataStyled>
                </TableRowStyled>
              )}
            </tbody>
          </TableStyled>
          <div>
            <ButtonStyled style={{marginLeft: '0px'}} onClick={() => forceRefresh("listings")}>Force Refresh Listings</ButtonStyled>
            <ButtonStyled style={{marginLeft: '0px'}} onClick={() => forceRefresh("rentcast")}>Force Refresh Rent Income</ButtonStyled>
            <ButtonStyled style={{marginLeft: '0px'}} onClick={() => forceRefresh("gemini")}>Force Refresh Market Data</ButtonStyled> 
          </div>
          {liveStatus && (
            <StatusMessageAreaStyled
              ref={liveStatusRef}
              style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #ccc", padding: "5px", marginTop: "10px" }}
            >
              {liveStatus.split("\n").map((msg, idx) => (
                <div key={idx}>{msg}</div>
              ))}
            </StatusMessageAreaStyled>
          )}
          
        </>
      )}
      <div>
        {errors.length > 0 ? 
            <div>
              {errors.map(((error, i) => (
                <ErrorMessageStyled key={i}>{error.message}</ErrorMessageStyled>
              )))}
            </div>:
            <></>
        }
      </div>
      <FormNavContainerStyled>
        <SecondaryButtonStyled onClick={handleDecrementFormStep}>Back</SecondaryButtonStyled>
        <PrimaryButtonStyled onClick={handleIncrementFormStep}>Next</PrimaryButtonStyled>
      </FormNavContainerStyled>
    
    </div>
  );
};

ZipDataTableMobile.propTypes = {
  mode: PropTypes.string.isRequired,
  handleIncrementFormStep: PropTypes.func.isRequired,
  handleDecrementFormStep: PropTypes.func.isRequired,
  zipCodes: PropTypes.array.isRequired, 
  formData: PropTypes.object.isRequired, 
  setFormData: PropTypes.func.isRequired
};

export default ZipDataTableMobile;